import { useNavigate, useLocation } from 'react-router-dom'
import isEmpty from 'lodash/isEmpty'
import { css, cx } from '@fable/theme'
import { Author, Book, Folio } from '@fable/types'
import ChevronIcon from '../../../../assets/icons/components/ChevronIcon'

import {
  AuthorMasthead,
  BookListMasthead,
  FolioMasthead,
  GenreMasthead,
} from './variants'

import { routes } from '../../routes'

import { BookList, ContentTypes, Genre } from '../../../../types'
import './DetailMasthead.scss'

type MastheadInfo = Book | Author | Folio | Genre | BookList

interface Props {
  info: MastheadInfo
  type: ContentTypes
  color: string
  hasGradient?: boolean
}

const DetailMasthead: React.FC<Props> = ({
  info,
  type,
  color,
  hasGradient,
}) => {
  const navigate = useNavigate()
  const location = useLocation()

  const getDefaultBackBtnAttr = () => {
    let path
    let title

    switch (type) {
      case ContentTypes.author:
        path = routes.explore
        title = 'Explore'
        break
      case ContentTypes.folio:
        path = routes.folios
        title = 'Folios'
        break
      case ContentTypes.genres:
        path = routes.genres
        title = 'All genres'
        break
      default:
        path = routes.explore
        title = 'Explore'
    }

    return { path, title }
  }

  const renderBackButton = () => {
    let buttonTitle = ''
    let buttonLink = ''

    const locationState = location.state as { prevPath: string }

    if (!isEmpty(locationState)) {
      buttonTitle = 'Back'
      buttonLink = locationState.prevPath
    } else {
      buttonTitle = getDefaultBackBtnAttr().title
      buttonLink = getDefaultBackBtnAttr().path
    }

    return (
      <div
        className="back-button button solid mini"
        onClick={() => navigate(buttonLink)}
      >
        <ChevronIcon />
        {buttonTitle}
      </div>
    )
  }

  const renderContent = () => {
    let mastheadClass
    let content

    switch (type) {
      case ContentTypes.author:
        mastheadClass = 'author'
        content = <AuthorMasthead info={info as Author} />
        break
      case ContentTypes.folio:
        mastheadClass = 'folio'
        content = <FolioMasthead info={info as Folio} />
        break
      case ContentTypes.bookList:
        mastheadClass = 'book-list'
        content = <BookListMasthead info={info as BookList} />
        break
      case ContentTypes.genres:
        mastheadClass = 'genre'
        content = <GenreMasthead info={info as Genre} />
        break
      default:
        break
    }

    return (
      <div
        className={cx(
          styles.masthead,
          `detail-masthead ${mastheadClass}-masthead`
        )}
        style={{ backgroundColor: color }}
      >
        {hasGradient && <span className={cx(styles.gradient)}></span>}
        {renderBackButton()}
        <div className={cx(styles.content)}>{content}</div>
      </div>
    )
  }

  return renderContent()
}

export default DetailMasthead

const styles = {
  masthead: css`
    position: relative;
  `,
  gradient: css`
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background: linear-gradient(180deg, #161015 0%, rgba(22, 16, 21, 0) 100%);
    opacity: 0.4;
  `,
  content: css`
    position: relative;
  `,
}
